import { DefaultLayout } from "@westpierstudio/luma/layout";
import { AssetSpec, SpriteAssetSpec, TextAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { Sprite, Text } from "pixi.js";
import { Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";
import { getSoundPlayer } from "sound/SoundLocator";
import { Resettable } from "@westpierstudio/luma/utils/reset";
import { BoundedContainer, Bounds, implementBoundedContainer } from "utils/BoundedContainer";
import { OrientationSpec } from "servertypes/SharedTypes";

interface TotalWinPanelSpriteSpec extends SpriteAssetSpec {
    position: OrientationSpec<AssetSpec>
    textBounds: Bounds
    noWinsTextBounds: Bounds
    totalWinPanel: SpriteAssetSpec;
    totalWinTopText: TextAssetSpec;
    totalWinBottomText: TextAssetSpec;
    totalWinBonusText: TextAssetSpec;
    totalNoWinsText: TextAssetSpec;
    totalWinTotalDisplayTime: number
}

class TotalWinPanelSignals {
    complete = new Signal();
    skip = new Signal();
}

export class TotalWinPanel extends View implements Resettable {
    private spec: TotalWinPanelSpriteSpec;
    private sprite: Sprite;

    private topText: Text;
    private bottomText: Text;
    private bonusText: Text;
    private bonusBounds: BoundedContainer;
    private noWinsText: Text;

    signals = new TotalWinPanelSignals();

    constructor(protected override readonly services: GameServices, layout: DefaultLayout) {
        super(services);
        const { factory, resources } = this.services;

        this.spec = resources.getSpec(`gamewinpanels`);
        factory.applySpec(this, this.spec.position[layout.orientation]);

        this.sprite = factory.createSpriteFromTextureKey(`totalWinPanel`, {}, this);
        this.sprite.x -= this.sprite.width / 2;
        this.sprite.y -= this.sprite.height / 2;
        implementBoundedContainer(this.topText = factory.createText(resources.getMessage(`totalWinTopText`), this.spec.totalWinTopText, this), this.spec.textBounds);
        this.bonusBounds = implementBoundedContainer(this.bonusText = factory.createText(`000`, this.spec.totalWinBonusText, this), this.spec.textBounds);
        implementBoundedContainer(this.bottomText = factory.createText(resources.getMessage(`totalWinBottomText`), this.spec.totalWinBottomText, this), this.spec.textBounds);
        implementBoundedContainer(this.noWinsText = factory.createText(resources.getMessage(`totalWinNoWinsText`), this.spec.totalNoWinsText, this), this.spec.noWinsTextBounds);
        this.noWinsText.visible = false;

        this.signals.skip.add(() => this.slam());

        this.hide();

        layout.onReorient.add(() => this.reorient());
    }

    private reorient() {
        const { factory, layout } = this.services;
        factory.applySpec(this, this.spec.position[layout.orientation]);
    }

    show(bonusWin: number, bet: number) {
        if (bonusWin > 0) {
            this.topText.visible = true;
            this.noWinsText.visible = false;
            this.bottomText.visible = true;
            this.bonusText.text = this.services.currency.formatCoins(
                bonusWin,
                { useSubUnits: true, group: true, addTrailingZeros: true, trimIntegers: false }
            );;
            this.bonusBounds.update();
            getSoundPlayer().bonusTotal(bonusWin / bet);
            Tween.get(this)
                .wait(this.spec.totalWinTotalDisplayTime)
                .call(() => {
                    this.hide();
                    this.signals.complete.dispatch();
                });
        }
        else {
            //Special case for no wins see "Reference\Bonus\PrinceOfThieves_S9 - Bonus - No Wins.mp4"
            this.bonusText.text = ``;
            this.topText.visible = false;
            this.noWinsText.visible = true;
            this.bottomText.visible = false;

            getSoundPlayer().bonusTotal(bonusWin / bet);
            Tween.get(this)
                .wait(this.spec.totalWinTotalDisplayTime)
                .call(() => {
                    this.hide();
                    this.noWinsText.visible = false;
                    this.bottomText.visible = true;
                    this.topText.visible = true;
                    this.signals.complete.dispatch();
                });
        }
        this.fade(0, 1, 500);
        this.visible = true;

    }

    hide() {
        this.visible = false;
    }

    slam() {
        if (this.visible) {
            Tween.removeTweens(this);
            this.signals.complete.dispatch();
            this.hide();
        }
    }

    private fade(start: number, end: number, duration: number, cb?: () => void) {
        this.alpha = start;
        Tween.get(this)
            .to({ alpha: end }, duration)
            .call(() => {
                if (cb) cb();
            });
    }

    override reset(): void {
        Tween.removeTweens(this);
        this.hide();
    }
}